<script lang="ts">
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import {
    QuickOrderListDetail,
    QuickOrderListIndex,
    ProtectedListName,
    ListType,
  } from "@shared/types/QuickOrderList";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import type { RetailerType } from "@shared/types/Retailer";

  export let retailer: RetailerType;
  export let lists: QuickOrderListIndex[];
  export let currentList: QuickOrderListDetail;
  export let currentListType: ListType;

  let createdLists: QuickOrderListIndex[];

  let autogeneratedListNames: string[]; // TODO: Remove after safe merge of PreviouslyOrdered Changes
  let autogeneratedListTypes: string[];

  if (stagedRolloutCheck("marketplace__previously_ordered_autogenerated_lists")) {
    autogeneratedListTypes = [ListType.PreviouslyOrdered, ListType.Sculpture];
  } else {
    autogeneratedListNames = [
      ProtectedListName.RecentlyOrdered as string,
      ProtectedListName.SculptureProducts as string,
    ];
  }

  let limitedOrderingRetailer =
    retailer.limited_ordering && stagedRolloutCheck("adm__dynamic_limited_ordering_filters");

  let approvedProductsLinkText = limitedOrderingRetailer
    ? "Approved products"
    : "My saved products";
  let approvedProductsListType = limitedOrderingRetailer
    ? ListType.ApprovedProducts
    : ListType.MySavedProducts;

  let sculptureProductsList: QuickOrderListIndex;
  let recentlyOrderedList: QuickOrderListIndex; // TODO: Remove after safe merge of PreviouslyOrdered Changes
  let previouslyOrderedList: QuickOrderListIndex;

  let defaultState = {
    category: null,
    container: null,
    direction: null,
    distributor: null,
    smart_filter_distributor: null,
    product_type: null,
    search: null,
    sort: null,
    subcategory: null,
    page: null,
    listId: null,
    distributor_ids: null,
  };

  $: {
    currentList = currentList;
    sculptureProductsList = lists.find(list => list.name === ProtectedListName.SculptureProducts);
    if (stagedRolloutCheck("marketplace__previously_ordered_autogenerated_lists")) {
      createdLists = lists.filter(list => !autogeneratedListTypes.includes(list.list_type));
      previouslyOrderedList = lists.find(list => list.list_type == ListType.PreviouslyOrdered);
    } else {
      createdLists = lists.filter(list => !autogeneratedListNames.includes(list.name));
      recentlyOrderedList = lists.find(list => list.list_type === ListType.RecentlyOrdered);
    }
  }
</script>

<ul
  class="fizz-stack-12 fizz-flow-4 fizz-border-bottom fizz-inset-vertical-12"
  style="padding-top: 0"
>
  {#if recentlyOrderedList}
    <div class="fizz-radius-none fizz-border-bottom fizz-stack-12">
      <UIRouterLink
        stateName="layout.list_details"
        params={{ ...defaultState, listId: recentlyOrderedList.id }}
        classes={"fizz-text-inherit fizz-underline-none fizz-stack-12"}
        id="recently_ordered"
      >
        <li
          class="fizz-inset-horizontal-12 fizz-inset-vertical-8 fizz-expand-12 fizz-highlight-on-hover fizz-stack-12 fizz-radius-medium"
          class:fizz-surface-gray={currentListType === ListType.RecentlyOrdered}
          class:fizz-text-strong={currentListType === ListType.RecentlyOrdered}
          data-testid="recently-ordered-sidebar-option"
        >
          <span class="cursor-pointer fizz-flex-row">
            <Icon
              type="History"
              className="fizz-text-brand cursor-pointer fizz-inline-8"
              height="16"
              width="16"
            />Recently ordered</span
          >
        </li>
      </UIRouterLink>
    </div>
  {/if}
  {#if previouslyOrderedList}
    <div class="fizz-radius-none fizz-border-bottom fizz-stack-12">
      <UIRouterLink
        stateName="layout.list_details"
        params={{
          ...defaultState,
          listId: previouslyOrderedList.id,
          sort: "last_ordered_at",
          direction: "DESC",
        }}
        classes={"fizz-text-inherit fizz-underline-none fizz-stack-12"}
        id="previously_ordered"
      >
        <li
          class="fizz-inset-horizontal-12 fizz-inset-vertical-8 fizz-expand-12 fizz-highlight-on-hover fizz-stack-12 fizz-radius-medium"
          class:fizz-surface-gray={currentListType === ListType.PreviouslyOrdered}
          class:fizz-text-strong={currentListType === ListType.PreviouslyOrdered}
          data-testid="previously-ordered-sidebar-option"
        >
          <span class="cursor-pointer fizz-flex-row">
            <Icon
              type="History"
              className="fizz-text-brand cursor-pointer fizz-inline-8"
              height="16"
              width="16"
            />{previouslyOrderedList.name}</span
          >
        </li>
      </UIRouterLink>
    </div>
  {/if}
  <UIRouterLink
    stateName="layout.quick_order_list_index"
    params={defaultState}
    classes={"fizz-text-inherit fizz-underline-none"}
    id="my-saved-products"
  >
    <li
      class="fizz-inset-horizontal-12 fizz-inset-vertical-8 fizz-expand-12 fizz-highlight-on-hover fizz-clamp fizz-radius-medium"
      class:fizz-surface-gray={currentListType === approvedProductsListType}
      class:fizz-text-strong={currentListType === approvedProductsListType}
      data-testid="my-saved-products-sidebar-option"
    >
      <span class="cursor-pointer fizz-flex-row"
        ><Icon
          type="Lists"
          className="fizz-text-brand cursor-pointer fizz-inline-8"
          height="16"
          width="16"
        />{approvedProductsLinkText}</span
      >
    </li>
  </UIRouterLink>
  {#if sculptureProductsList}
    <UIRouterLink
      stateName="layout.list_details"
      params={{ ...defaultState, listId: sculptureProductsList.id }}
      classes={"fizz-text-inherit fizz-underline-none"}
      id="sculpture_list"
    >
      <li
        class="fizz-inset-horizontal-12 fizz-inset-vertical-8 fizz-expand-12 fizz-highlight-on-hover fizz-clamp fizz-radius-medium"
        class:fizz-surface-gray={currentListType === ListType.Sculpture}
        class:fizz-text-strong={currentListType === ListType.Sculpture}
        data-testid="sculpture-products-sidebar-option"
      >
        <span class="cursor-pointer">{sculptureProductsList.name}</span>
      </li>
    </UIRouterLink>
  {/if}
</ul>
<p class="fizz-text-eyebrow fizz-stack-8 fizz-text-subdued">My lists</p>
{#if createdLists.length == 0}
  <p class="fizz-text-subdued fizz-text-small fizz-stack-8">No lists have been created yet</p>
{/if}
<ul class="fizz-flow-4">
  {#each createdLists as list}
    <UIRouterLink
      stateName="layout.list_details"
      params={{ ...defaultState, listId: list.id }}
      classes={"fizz-text-inherit fizz-underline-none"}
    >
      <li
        class="fizz-inset-horizontal-12 fizz-inset-vertical-8 fizz-expand-12 fizz-radius-medium fizz-highlight-on-hover"
        class:fizz-surface-gray={currentList.name == list.name}
        class:fizz-text-strong={currentList.name == list.name}
        data-testid="user-created-list-{list.id}-sidebar-option"
      >
        <span class="cursor-pointer fizz-clamp">{list.name}</span>
      </li>
    </UIRouterLink>
  {/each}
</ul>
